import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function WorkingHolidayVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "work-in-vancouver-workin-holiday-visa";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Eligibility & Application Process",
    "section-2": "Best Job Industries & Opportunities",
    "section-3": "Key Locations in Vancouver",
    "local-regulations": "Local Regulations & Best Practices",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.canada.ca/en/immigration-refugees-citizenship/services/work-canada/iec.html",
      label: "Government of Canada – International Experience Canada (IEC)",
    },
    {
      url: "https://www.workbc.ca/Jobs-Careers/Find-Jobs/Jobs.aspx",
      label: "WorkBC – Find Jobs in BC",
    },
    {
      url: "https://www.jobbank.gc.ca",
      label: "Government of Canada Job Bank",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed, each with a unique "id" and lat/lng.
  const locations = [
    {
      id: "service-canada",
      lat: 49.28600,
      lng: -123.11390,
      popupText: "Service Canada Centre – Vancouver (Sinclair Centre)",
    },
    {
      id: "library",
      lat: 49.27979,
      lng: -123.11567,
      popupText: "Vancouver Public Library – Central Branch",
    },
    {
      id: "iss-of-bc",
      lat: 49.26105,
      lng: -123.06568,
      popupText: "ISSofBC Welcome Centre",
    },
    {
      id: "downtown",
      lat: 49.28270,
      lng: -123.12070,
      popupText: "Downtown Vancouver (City Centre)",
    },
    {
      id: "gastown",
      lat: 49.28469,
      lng: -123.11095,
      popupText: "Gastown Historic District",
    },
    {
      id: "canada-place",
      lat: 49.28880,
      lng: -123.11125,
      popupText: "Canada Place & Waterfront",
    },
    {
      id: "work-bc-mount-pleasant",
      lat: 49.26350,
      lng: -123.11400,
      popupText: "WorkBC Employment Services Centre (Mount Pleasant)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Work in Vancouver: Your Complete Working Holiday Visa Guide</title>
        <meta
          name="description"
          content="Learn how to apply for a Working Holiday Visa in Vancouver. Get tips on eligibility, job hunting, local regulations, key locations, and FAQs."
        />
        <meta
          name="keywords"
          content="Vancouver, Working Holiday Visa, IEC, job hunting, Canada"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/work-in-vancouver-workin-holiday-visa.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Work in Vancouver: Your Complete Working Holiday Visa Guide"
        />
        <meta
          property="og:description"
          content="A thorough guide on obtaining a Working Holiday Visa in Vancouver, including steps, tips, local laws, and resources."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/work-in-vancouver-workin-holiday-visa.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/work-in-vancouver-workin-holiday-visa.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Work in Vancouver: Your Complete Working Holiday Visa Guide"
        />
        <meta
          name="twitter:description"
          content="Discover everything you need to know about the IEC Working Holiday in Vancouver. Step-by-step tips, job info, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/work-in-vancouver-workin-holiday-visa.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Vancouver skyline with mountains in the background"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* =========================
                SECTION: Introduction
            ========================== */}
            <h2 id="intro">Introduction</h2>
            <p>
              A <strong>Working Holiday Visa</strong> is a special permit that
              allows young travelers to work and explore a foreign country
              simultaneously. Canada’s program, known as{" "}
              <em>International Experience Canada (IEC)</em>, offers a unique
              opportunity to live and work in Vancouver—one of the world’s most
              picturesque and multicultural cities. Whether you want to develop
              your professional skills, fund your travels, or enjoy an
              adventure, Vancouver provides a diverse job market and scenic
              surroundings that can make your working holiday unforgettable.
            </p>

            {/* =========================
                SECTION 1: Eligibility & Application Process
            ========================== */}
            <h2 id="section-1">Eligibility &amp; Application Process</h2>
            <p>
              Before heading to Vancouver, ensure you meet the eligibility
              requirements and are familiar with the step-by-step application
              process. Most participants must be aged 18 to 30 or 35 (depending
              on their home country) and hold a valid passport from a country
              with a youth mobility agreement with Canada. You will also need
              proof of funds, health insurance, and other supporting documents.
              Below is an overview of the main stages in applying for a
              <em>Working Holiday Visa</em> under the IEC program:
            </p>
            <ul>
              <li>
                <strong>Create an IEC Profile</strong> on the official IRCC
                website.
              </li>
              <li>
                <strong>Join the Pool</strong> for your country’s Working
                Holiday category.
              </li>
              <li>
                Receive an <em>Invitation to Apply (ITA)</em> and submit your
                work permit application with required documents.
              </li>
              <li>
                Pay the application fees (including biometrics fees, if
                applicable).
              </li>
              <li>
                Wait for your <em>Port of Entry (POE)</em> letter, then prepare
                documents for arrival.
              </li>
            </ul>
            <p>
              Once you land in Vancouver, an immigration officer will issue your
              work permit if all conditions are met. Remember, the work permit
              typically lasts up to 12 or 24 months, depending on your country
              of citizenship.
            </p>

            {/* =========================
                SECTION 2: Best Job Industries & Opportunities
            ========================== */}
            <h2 id="section-2">Best Job Industries &amp; Opportunities</h2>
            <p>
              Vancouver’s diverse economy and booming tourism industry make it
              an ideal location for working holiday participants looking for
              flexible or seasonal jobs. Key industries include:
            </p>
            <ul>
              <li>
                <strong>Hospitality &amp; Tourism</strong> – Hotels, restaurants,
                and tour operators frequently hire seasonal or short-term staff.
              </li>
              <li>
                <strong>Retail &amp; Customer Service</strong> – Major shopping
                districts and malls often need sales associates, cashiers, and
                merchandisers.
              </li>
              <li>
                <strong>Outdoor Adventure &amp; Ski Resorts</strong> – Popular
                for those seeking jobs in lift operations, ski instruction, or
                adventure tours.
              </li>
              <li>
                <strong>Office Administration &amp; Temp Work</strong> – Agencies
                like Randstad or Adecco place workers in data entry or reception
                roles.
              </li>
              <li>
                <strong>Technology &amp; Startups</strong> – A burgeoning scene
                that values networking and co-working spaces.
              </li>
              <li>
                <strong>Film &amp; Entertainment</strong> – Known as
                “Hollywood North,” Vancouver offers opportunities for extras and
                production assistants.
              </li>
            </ul>
            <p>
              A typical approach is to arrive in Vancouver, secure housing, and
              then begin distributing your resume in person or applying online
              through job boards. Be aware of the relatively high cost of living
              and plan your finances accordingly.
            </p>

            {/* =========================
                SECTION 3: Key Locations in Vancouver
            ========================== */}
            <h2 id="section-3">Key Locations in Vancouver</h2>
            <p>
              Familiarize yourself with these important places for paperwork,
              job hunting, and networking. Each location can serve as a
              launching point to help you get settled or find new opportunities.
            </p>

            {/* Service Canada - get SIN */}
            <p
              ref={(el) => setSpotRef("service-canada", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Service Canada Centre – Vancouver (Sinclair Centre)</strong>{" "}
              <Marker
                coordY={49.28600}
                coordX={-123.11390}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – The first stop for obtaining your Social Insurance Number (SIN),
              which you need before starting work. It’s conveniently located in
              downtown Vancouver.
            </p>

            {/* Vancouver Public Library */}
            <p
              ref={(el) => setSpotRef("library", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vancouver Public Library – Central Branch</strong>{" "}
              <Marker
                coordY={49.27979}
                coordX={-123.11567}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – A great spot for free Wi-Fi, job search resources, and community
              workshops tailored for newcomers. The Skilled Immigrant InfoCentre
              here can provide job search assistance.
            </p>

            {/* ISSofBC */}
            <p
              ref={(el) => setSpotRef("iss-of-bc", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>ISSofBC Welcome Centre</strong>{" "}
              <Marker
                coordY={49.26105}
                coordX={-123.06568}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – This hub offers settlement services and can guide you through
              English language support, job workshops, and general newcomer
              assistance.
            </p>

            {/* Downtown Vancouver */}
            <p
              ref={(el) => setSpotRef("downtown", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Downtown Vancouver (City Centre)</strong>{" "}
              <Marker
                coordY={49.28270}
                coordX={-123.12070}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – The bustling core of the city, filled with offices, hotels, and
              retail stores. Ideal for dropping off resumes and attending
              interviews.
            </p>

            {/* Gastown */}
            <p
              ref={(el) => setSpotRef("gastown", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Gastown Historic District</strong>{" "}
              <Marker
                coordY={49.28469}
                coordX={-123.11095}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – A trendy and historic neighborhood with a lively bar and
              restaurant scene, making it a hot spot for hospitality jobs.
            </p>

            {/* Canada Place */}
            <p
              ref={(el) => setSpotRef("canada-place", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Canada Place & Waterfront</strong>{" "}
              <Marker
                coordY={49.28880}
                coordX={-123.11125}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Home to the Vancouver Convention Centre and cruise ship
              terminals, where seasonal tourist jobs or events roles are
              plentiful.
            </p>

            {/* WorkBC Mount Pleasant */}
            <p
              ref={(el) => setSpotRef("work-bc-mount-pleasant", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>WorkBC Employment Services Centre (Mount Pleasant)</strong>{" "}
              <Marker
                coordY={49.26350}
                coordX={-123.11400}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – One of several government-funded resource centers offering job
              listings, career counseling, and guidance for local residents and
              newcomers alike.
            </p>

            {/* =========================
                SECTION: Local Regulations & Best Practices
            ========================== */}
            <h2 id="local-regulations">Local Regulations &amp; Best Practices</h2>
            <p>
              During your working holiday, be mindful of basic legal guidelines
              and best practices:
            </p>
            <ul>
              <li>
                <strong>Work Permit Conditions:</strong> The IEC Working Holiday
                permit is typically an <em>open work permit</em>, valid only for
                a certain duration (12 or 24 months). Follow any restrictions
                listed.
              </li>
              <li>
                <strong>Minimum Wage:</strong> British Columbia’s minimum wage
                currently stands around CAD $17.40/hour (subject to change).
                Employers must pay you at least this rate.
              </li>
              <li>
                <strong>Social Insurance Number (SIN):</strong> You must obtain
                a SIN before starting any job. Always keep your SIN confidential
                and secure.
              </li>
              <li>
                <strong>Taxes:</strong> Employers deduct income tax, CPP, and EI
                from your paycheck. You are responsible for filing a Canadian
                tax return if you earn income.
              </li>
              <li>
                <strong>Health Insurance:</strong> IEC participants must have
                travel health insurance for the entire stay. Some may also
                qualify for BC’s Medical Services Plan, but always maintain
                private coverage.
              </li>
              <li>
                <strong>Housing:</strong> Vancouver’s rental market is
                competitive and pricey. Many working holidaymakers initially
                stay in hostels or shared housing to save costs.
              </li>
            </ul>

            {/* =========================
                SECTION: FAQ
            ========================== */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. How long can I work in Vancouver with an IEC Working Holiday Visa?</h3>
              <p>
                Most people can stay and work for up to 12 months, but some
                nationalities receive 24-month permits. Always check your
                country’s specific agreement with Canada.
              </p>

              <h3>2. Do I need a job offer to apply?</h3>
              <p>
                No. The Working Holiday Visa is an <em>open work permit</em>,
                which means you do not need a job offer in advance. You can
                search for work after arriving.
              </p>

              <h3>3. Is Vancouver expensive to live in?</h3>
              <p>
                Vancouver is one of Canada’s priciest cities, especially for
                housing. Plan your budget accordingly, and consider shared
                accommodations to reduce costs.
              </p>
            </div>

            {/* =========================
                SECTION: Conclusion
            ========================== */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              A working holiday in Vancouver can be an enriching experience for
              anyone seeking adventure, cultural exchange, and professional
              growth. From exploring vibrant neighborhoods like Gastown to
              skiing in nearby mountains, you will find no shortage of things to
              do on your days off. Meanwhile, a thriving tourism industry and
              diverse economy mean plenty of job options. Always check official
              resources for up-to-date regulations, maintain valid insurance,
              and embrace the opportunities that come with living in this
              world-class city.
            </p>
            <p>
              <strong>Disclaimer:</strong> This guide is for informational
              purposes and does not substitute official immigration or legal
              advice. Always verify up-to-date requirements through government
              websites and consult professionals if needed. Stay safe, follow
              local regulations, and enjoy your Vancouver working holiday!
            </p>

            {/* ============ BOTTOM: references ============ */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}